import { forwardRef, useState, useCallback } from 'react';
import type { Place } from '../types/Place';
import { getCountryEmoji } from '../utils/countryEmoji';

interface JournalEntryProps {
  place: Place;
  isHighlighted?: boolean;
  onShowOnMap: () => void;
}


export const JournalEntry = forwardRef<HTMLDivElement, JournalEntryProps>(
  function JournalEntry({ place, isHighlighted = false, onShowOnMap }, ref) {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const handleImageError = useCallback(() => {
      setImageError(true);
    }, []);

    const handleImageLoad = useCallback(() => {
      setImageLoaded(true);
    }, []);

    return (
      <div
        ref={ref}
        className={`relative ml-12 md:ml-16 transition-all duration-500 ${
          isHighlighted
            ? 'ring-2 ring-primary ring-offset-2 ring-offset-bg-base rounded-lg shadow-[0_0_20px_rgba(139,92,246,0.3)]'
            : ''
        }`}
        data-testid={`journal-entry-${place.id}`}
      >
        {/* Timeline dot */}
        <div
          className="absolute -left-[42px] md:-left-[54px] top-2 w-3 h-3 rounded-full bg-primary shadow-[0_0_10px_rgba(139,92,246,0.5)]"
          aria-hidden="true"
          data-testid="timeline-dot"
        />

        {/* Entry content */}
        <div className="bg-bg-surface rounded-lg p-4 transition-colors duration-fast lg:hover:bg-bg-surface/80">
          {/* Date */}
          <time
            dateTime={place.date}
            className="text-sm text-gray-500 uppercase tracking-wide"
            data-testid="entry-date"
          >
            {place.dateDisplay ?? place.date}
          </time>

          {/* Location with emoji */}
          <h3 className="text-lg font-semibold text-white mt-1" data-testid="entry-location">
            {getCountryEmoji(place.country)} {place.location}
          </h3>

          {/* Story/Hook text */}
          <p className="text-gray-400 mt-2 leading-relaxed italic" data-testid="entry-hook">
            {place.hook}
          </p>

          {/* Photo */}
          <div
            className={`mt-4 aspect-video rounded-lg overflow-hidden bg-bg-base ${
              imageError ? 'photo-placeholder' : ''
            }`}
            data-testid="entry-photo-container"
          >
            {/* Loading skeleton */}
            {!imageError && !imageLoaded && (
              <div
                className="w-full h-full animate-pulse bg-gray-700"
                data-testid="entry-photo-skeleton"
                aria-label="Loading image"
              />
            )}
            {!imageError && (
              <img
                src={place.photos[0]}
                alt={place.location}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
                onError={handleImageError}
                onLoad={handleImageLoad}
                data-testid="entry-photo"
              />
            )}
          </div>

          {/* Show on Map button */}
          <button
            onClick={onShowOnMap}
            className="mt-4 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors min-h-[44px]"
            aria-label={`Show ${place.location} on map`}
            data-testid="show-on-map-button"
          >
            <span>🗺️</span>
            <span>Show on Map</span>
          </button>
        </div>
      </div>
    );
  }
);
